/**
 * BorderTab — border width/style/color, border radius, outline, and box shadow.
 * Single responsibility: border and outline style properties.
 */

import React, { useState } from 'react';
import type { StyleProperties } from '../types';
import { ColorPicker } from '../shared/ColorPicker';
import { NumberInput } from '../shared/NumberInput';
import { SliderInput } from '../shared/SliderInput';
import { SegmentedControl } from '../shared/SegmentedControl';
import { PresetChips } from '../shared/PresetChips';
import { SectionLabel } from '../shared/SectionLabel';

export interface BorderTabProps {
  value: StyleProperties;
  onChange: (p: StyleProperties) => void;
  isDark: boolean;
}

const BORDER_STYLE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'solid', label: 'Solid' },
  { value: 'dashed', label: 'Dashed' },
  { value: 'dotted', label: 'Dotted' },
  { value: 'double', label: 'Double' },
] as const;

const BORDER_SIDE_STYLE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'solid', label: 'S' },
  { value: 'dashed', label: 'D' },
  { value: 'dotted', label: 'Dt' },
] as const;

const OUTLINE_STYLE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'solid', label: 'Solid' },
  { value: 'dashed', label: 'Dashed' },
  { value: 'dotted', label: 'Dotted' },
] as const;

const BOX_SHADOW_PRESETS = [
  { label: 'None', value: 'none' },
  { label: 'XS', value: '0 1px 2px rgba(0,0,0,0.05)', title: '0 1px 2px rgba(0,0,0,0.05)' },
  { label: 'SM', value: '0 1px 3px rgba(0,0,0,0.1),0 1px 2px rgba(0,0,0,0.06)', title: 'Small shadow' },
  { label: 'MD', value: '0 4px 6px -1px rgba(0,0,0,0.1),0 2px 4px -2px rgba(0,0,0,0.1)', title: 'Medium shadow' },
  { label: 'LG', value: '0 10px 15px -3px rgba(0,0,0,0.1)', title: 'Large shadow' },
  { label: 'XL', value: '0 20px 25px -5px rgba(0,0,0,0.1)', title: 'Extra large shadow' },
  { label: 'Inner', value: 'inset 0 2px 4px rgba(0,0,0,0.1)', title: 'Inset shadow' },
];

type SideMode = 'all' | 'individual';
type RadiusMode = 'all' | 'individual';

/**
 * BorderTab — borders, radius, outline, and box shadow.
 */
export const BorderTab: React.FC<BorderTabProps> = ({ value, onChange, isDark }) => {
  const [borderSideMode, setBorderSideMode] = useState<SideMode>('all');
  const [radiusMode, setRadiusMode] = useState<RadiusMode>('all');

  const textPrimary = isDark ? '#e2e8f0' : '#1e293b';
  const inputBg = isDark ? 'rgba(2,6,23,0.6)' : '#f8fafc';
  const borderColor = isDark ? 'rgba(51,65,85,0.5)' : '#e2e8f0';
  const textSecondary = isDark ? '#94a3b8' : '#64748b';
  const accent = isDark ? '#22c55e' : '#16a34a';

  const stackStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  };

  const toggleBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: '3px 8px',
    borderRadius: 4,
    border: `1px solid ${active ? accent : borderColor}`,
    background: active ? accent : 'transparent',
    color: active ? '#ffffff' : textSecondary,
    cursor: 'pointer',
    fontSize: 11,
    fontWeight: active ? 600 : 400,
  });

  const modeToggleStyle: React.CSSProperties = {
    display: 'flex',
    gap: 4,
    marginBottom: 6,
  };

  const customShadowInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '5px 8px',
    borderRadius: 4,
    border: `1px solid ${borderColor}`,
    background: inputBg,
    color: textPrimary,
    fontSize: 12,
    boxSizing: 'border-box',
    fontFamily: 'monospace',
    marginTop: 4,
  };

  const sideRowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '24px 1fr auto auto',
    alignItems: 'center',
    gap: 4,
    marginBottom: 4,
  };

  const sideLabelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    color: textSecondary,
  };

  type IndividualSide = 'Top' | 'Right' | 'Bottom' | 'Left';

  const SideRow = ({ side }: { side: IndividualSide }) => {
    const widthField = `border${side}Width` as keyof StyleProperties;
    const styleField = `border${side}Style` as keyof StyleProperties;
    const colorField = `border${side}Color` as keyof StyleProperties;

    return (
      <div style={sideRowStyle}>
        <span style={sideLabelStyle}>{side[0]}</span>
        <NumberInput
          label=""
          value={value[widthField] as number | undefined}
          onChange={(v) => onChange({ ...value, [widthField]: v })}
          unit="px"
          min={0}
          isDark={isDark}
        />
        <SegmentedControl
          options={BORDER_SIDE_STYLE_OPTIONS as unknown as Array<{ value: string; label: string }>}
          value={value[styleField] as string | undefined}
          onChange={(v) => onChange({ ...value, [styleField]: v })}
          isDark={isDark}
        />
        <ColorPicker
          label={`${side} border color`}
          value={value[colorField] as string | undefined}
          onChange={(v) => onChange({ ...value, [colorField]: v })}
          isDark={isDark}
        />
      </div>
    );
  };

  const cornerGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 6,
  };

  return (
    <div style={stackStyle}>
      <SectionLabel label="Border" isDark={isDark} />

      {/* Mode toggle */}
      <div style={modeToggleStyle}>
        <button
          type="button"
          style={toggleBtnStyle(borderSideMode === 'all')}
          onClick={() => setBorderSideMode('all')}
          aria-pressed={borderSideMode === 'all'}
          aria-label="All sides border mode"
        >
          All Sides
        </button>
        <button
          type="button"
          style={toggleBtnStyle(borderSideMode === 'individual')}
          onClick={() => setBorderSideMode('individual')}
          aria-pressed={borderSideMode === 'individual'}
          aria-label="Individual sides border mode"
        >
          Individual
        </button>
      </div>

      {borderSideMode === 'all' ? (
        <>
          <NumberInput
            label="Width"
            value={value.borderWidth}
            onChange={(v) => onChange({ ...value, borderWidth: v })}
            unit="px"
            min={0}
            isDark={isDark}
          />
          <SegmentedControl
            label="Style"
            options={BORDER_STYLE_OPTIONS as unknown as Array<{ value: string; label: string }>}
            value={value.borderStyle}
            onChange={(v) => onChange({ ...value, borderStyle: v as StyleProperties['borderStyle'] })}
            isDark={isDark}
          />
          <ColorPicker
            label="Color"
            value={value.borderColor}
            onChange={(v) => onChange({ ...value, borderColor: v })}
            isDark={isDark}
          />
        </>
      ) : (
        <>
          {(['Top', 'Right', 'Bottom', 'Left'] as IndividualSide[]).map((side) => (
            <SideRow key={side} side={side} />
          ))}
        </>
      )}

      <SectionLabel label="Border Radius" isDark={isDark} />

      {/* Radius mode toggle */}
      <div style={modeToggleStyle}>
        <button
          type="button"
          style={toggleBtnStyle(radiusMode === 'all')}
          onClick={() => setRadiusMode('all')}
          aria-pressed={radiusMode === 'all'}
          aria-label="All corners radius mode"
        >
          All Corners
        </button>
        <button
          type="button"
          style={toggleBtnStyle(radiusMode === 'individual')}
          onClick={() => setRadiusMode('individual')}
          aria-pressed={radiusMode === 'individual'}
          aria-label="Individual corners radius mode"
        >
          Individual
        </button>
      </div>

      {radiusMode === 'all' ? (
        <SliderInput
          label="Radius"
          value={value.borderRadius}
          onChange={(v) => onChange({ ...value, borderRadius: v })}
          min={0}
          max={50}
          step={1}
          unit="px"
          isDark={isDark}
        />
      ) : (
        <div style={cornerGridStyle}>
          {(
            [
              ['borderTopLeftRadius', 'TL'],
              ['borderTopRightRadius', 'TR'],
              ['borderBottomLeftRadius', 'BL'],
              ['borderBottomRightRadius', 'BR'],
            ] as const
          ).map(([field, lbl]) => (
            <NumberInput
              key={field}
              label={lbl}
              value={value[field]}
              onChange={(v) => onChange({ ...value, [field]: v })}
              unit="px"
              min={0}
              isDark={isDark}
            />
          ))}
        </div>
      )}

      <SectionLabel label="Outline (Focus Ring)" isDark={isDark} />
      <NumberInput
        label="Width"
        value={value.outlineWidth}
        onChange={(v) => onChange({ ...value, outlineWidth: v })}
        unit="px"
        min={0}
        isDark={isDark}
      />
      <SegmentedControl
        label="Style"
        options={OUTLINE_STYLE_OPTIONS as unknown as Array<{ value: string; label: string }>}
        value={value.outlineStyle}
        onChange={(v) => onChange({ ...value, outlineStyle: v as StyleProperties['outlineStyle'] })}
        isDark={isDark}
      />
      <ColorPicker
        label="Color"
        value={value.outlineColor}
        onChange={(v) => onChange({ ...value, outlineColor: v })}
        isDark={isDark}
      />
      <NumberInput
        label="Offset"
        value={value.outlineOffset}
        onChange={(v) => onChange({ ...value, outlineOffset: v })}
        unit="px"
        isDark={isDark}
      />

      <SectionLabel label="Box Shadow" isDark={isDark} />
      <PresetChips
        chips={BOX_SHADOW_PRESETS}
        value={value.boxShadow}
        onChange={(v) => onChange({ ...value, boxShadow: v === 'none' ? undefined : v })}
        isDark={isDark}
      />
      <input
        type="text"
        value={value.boxShadow ?? ''}
        onChange={(e) => onChange({ ...value, boxShadow: e.target.value || undefined })}
        placeholder="e.g. 0 4px 6px rgba(0,0,0,0.1)"
        style={customShadowInputStyle}
        aria-label="Custom box shadow value"
        spellCheck={false}
      />
    </div>
  );
};

BorderTab.displayName = 'BorderTab';
